import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MagnifyingGlass, Package, CheckCircle, Warning, UsersThree, MapTrifold, FilePdf, Eye,
} from "@phosphor-icons/react";
import RecordDetailDialog from "@/components/RecordDetailDialog";
import { fmtDateTime } from "@/lib/dates";

export default function WorkOrderLookupPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("id") || "";
  const [woInput, setWoInput] = useState(initial);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailId, setDetailId] = useState(null);

  const runLookup = async (id) => {
    const wo = (id ?? woInput).trim();
    if (!wo) return;
    setLoading(true);
    setError("");
    setResult(null);
    setParams({ id: wo });
    try {
      const r = await api.get("/records/lookup", { params: { work_order_id: wo } });
      setResult(r.data);
      if (r.data.record_count === 0) setError(`No records found for Work Order "${wo}".`);
    } catch (e) {
      setError(formatApiError(e));
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initial) runLookup(initial);
    // eslint-disable-next-line
  }, []);

  const submit = (e) => { e.preventDefault(); runLookup(); };

  const downloadReceipt = async (id) => {
    try {
      const res = await api.get(`/records/${id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="px-10 py-6">
          <div className="label-eyebrow">Lookup tool</div>
          <h1 className="font-display text-4xl tracking-tight leading-none mt-2">Work Order</h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-xl">
            Enter a Work Order ID. See the technician assigned, parts used, and any faulty components returned from the site.
          </p>
        </div>
      </div>

      <div className="p-10 space-y-8">
        <form onSubmit={submit} className="flex gap-2 max-w-xl" data-testid="lookup-form">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={woInput}
              onChange={(e) => setWoInput(e.target.value)}
              placeholder="e.g. WO-2026-0421"
              className="h-11 pl-10 rounded-none border-neutral-900 focus-visible:ring-0 font-mono"
              data-testid="lookup-input"
              autoFocus
            />
          </div>
          <Button type="submit" disabled={loading || !woInput.trim()} data-testid="lookup-submit" className="h-11 rounded-none bg-neutral-900 text-white hover:bg-neutral-800 px-6">
            {loading ? "Searching…" : "Look up"}
          </Button>
        </form>

        {error && !loading && (
          <div className="max-w-xl border border-neutral-300 bg-neutral-50 p-4 text-sm text-neutral-700" data-testid="lookup-error">
            {error}
          </div>
        )}

        {result && result.record_count > 0 && (
          <div className="space-y-8" data-testid="lookup-result">
            {/* Summary bar */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <SummaryCard icon={Package} label="Records" value={result.record_count} testid="stat-records" />
              <SummaryCard icon={UsersThree} label="Technicians" value={result.technicians.length} testid="stat-techs" />
              <SummaryCard icon={CheckCircle} label="Parts used" value={sumQty(result.parts_used)} testid="stat-used" />
              <SummaryCard icon={Warning} label="Faulty returned" value={sumQty(result.faulty_parts)} testid="stat-faulty" tone="amber" />
              <div className="border border-neutral-200 bg-white p-5" data-testid="stat-kind">
                <div className="label-eyebrow">Issued kind</div>
                <div className="mt-3 flex items-baseline gap-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">New</div>
                    <div className="font-display text-3xl leading-none">{result.kind_totals?.new ?? 0}</div>
                  </div>
                  <div className="pl-3 border-l border-neutral-200">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700">RB</div>
                    <div className="font-display text-3xl leading-none text-amber-700">{result.kind_totals?.rb ?? 0}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Technicians */}
            <section>
              <SectionHeader step="01" title="Technician(s) assigned" icon={UsersThree} />
              <ul className="border border-neutral-200 bg-white divide-y divide-neutral-100">
                {result.technicians.map((t) => (
                  <li key={t.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid={`lookup-tech-${t.id}`}>
                    <div>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-neutral-500 font-mono">{t.email}</div>
                    </div>
                    <div className="text-xs font-mono text-neutral-600 inline-flex items-center gap-1">
                      <MapTrifold size={12} weight="duotone" /> {t.region}
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {/* Parts used */}
            <section>
              <SectionHeader step="02" title="Parts used on this job" icon={Package} />
              <PartsTable items={result.parts_used} empty="No parts have been marked used yet." testid="parts-used-table" />
              {result.parts_unused.length > 0 && (
                <div className="mt-3 text-xs text-neutral-500">
                  Also returned unused: {result.parts_unused.map((p) => `${p.name}×${p.quantity}`).join(", ")}
                </div>
              )}
            </section>

            {/* By group breakdown */}
            {(result.by_group || []).length > 0 && (
              <section>
                <SectionHeader step="03" title="Breakdown by group" icon={Package} />
                <div className="border border-neutral-200 bg-white overflow-x-auto" data-testid="by-group-table">
                  <table className="w-full text-sm">
                    <thead className="border-b border-neutral-200 bg-neutral-50">
                      <tr className="text-left">
                        <th className="px-3 py-2 label-eyebrow">Group</th>
                        <th className="px-3 py-2 label-eyebrow text-right">Taken</th>
                        <th className="px-3 py-2 label-eyebrow text-right">Used</th>
                        <th className="px-3 py-2 label-eyebrow text-right">Faulty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.by_group.map((g) => (
                        <tr key={g.group} className="border-b border-neutral-100 last:border-0" data-testid={`by-group-row-${g.group}`}>
                          <td className="px-3 py-2 font-mono text-xs uppercase tracking-wider">{g.group}</td>
                          <td className="px-3 py-2 text-right font-mono">{g.taken || 0}</td>
                          <td className="px-3 py-2 text-right font-mono">{g.used || 0}</td>
                          <td className={`px-3 py-2 text-right font-mono ${g.faulty ? "text-amber-700 font-semibold" : ""}`}>{g.faulty || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Faulty parts */}
            <section>
              <SectionHeader step="04" title="Faulty parts returned from site" icon={Warning} tone="amber" />
              {result.faulty_parts.length === 0 ? (
                <div className="border border-neutral-200 bg-white p-6 text-sm text-neutral-500 text-center">
                  None reported. Nothing was brought back as defective for this work order.
                </div>
              ) : (
                <PartsTable items={result.faulty_parts} tone="amber" testid="faulty-parts-table" />
              )}
            </section>

            {/* Underlying records */}
            <section>
              <SectionHeader step="05" title="Underlying records" icon={Package} />
              <div className="border border-neutral-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-neutral-200 bg-neutral-50">
                    <tr className="text-left">
                      <th className="px-3 py-2 label-eyebrow">Receipt #</th>
                      <th className="px-3 py-2 label-eyebrow">Engineer</th>
                      <th className="px-3 py-2 label-eyebrow">Region</th>
                      <th className="px-3 py-2 label-eyebrow">Taken</th>
                      <th className="px-3 py-2 label-eyebrow">Returned</th>
                      <th className="px-3 py-2 label-eyebrow">Status</th>
                      <th className="px-3 py-2 label-eyebrow">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.records.map((r) => (
                      <tr key={r.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`lookup-record-${r.id}`}>
                        <td className="px-3 py-2 font-mono text-xs font-medium">{r.receipt_no || "—"}</td>
                        <td className="px-3 py-2 font-medium">{r.engineer_name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.region}</td>
                        <td className="px-3 py-2 font-mono text-xs">{fmtDateTime(r.taken_at)}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.returned_at ? fmtDateTime(r.returned_at) : "—"}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${r.status === "open" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <button onClick={() => setDetailId(r.id)} className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white" title="View" data-testid={`lookup-view-${r.id}`}>
                              <Eye size={14} weight="bold" />
                            </button>
                            <button onClick={() => downloadReceipt(r.id)} className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white" title="PDF Receipt" data-testid={`lookup-receipt-${r.id}`}>
                              <FilePdf size={14} weight="bold" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>

      <RecordDetailDialog
        recordId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}

function sumQty(items) {
  return (items || []).reduce((a, b) => a + (b.quantity || 0), 0);
}

function SummaryCard({ icon: Icon, label, value, testid, tone }) {
  const border = tone === "amber" ? "border-amber-400" : "border-neutral-200 hover:border-neutral-900";
  return (
    <div className={`border ${border} bg-white p-5 transition-colors`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="font-display text-4xl mt-3 leading-none tracking-tight">{value}</div>
        </div>
        <Icon size={20} weight="duotone" className={tone === "amber" ? "text-amber-700" : "text-neutral-900"} />
      </div>
    </div>
  );
}

function SectionHeader({ step, title, icon: Icon, tone }) {
  const color = tone === "amber" ? "text-amber-700" : "text-neutral-900";
  return (
    <div className="flex items-end justify-between mb-3">
      <div>
        <div className="label-eyebrow">{step}</div>
        <h2 className={`font-display text-2xl tracking-tight mt-1 ${color}`}>{title}</h2>
      </div>
      <Icon size={20} weight="duotone" className={color} />
    </div>
  );
}

function PartsTable({ items, empty, testid, tone }) {
  const border = tone === "amber" ? "border-amber-300" : "border-neutral-200";
  const bg = tone === "amber" ? "bg-amber-50" : "bg-white";
  return (
    <div className={`border ${border} ${bg}`} data-testid={testid}>
      {items.length === 0 ? (
        <div className="p-6 text-sm text-neutral-500 text-center">{empty || "None"}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className={`border-b ${border} bg-neutral-50/60`}>
            <tr className="text-left">
              <th className="px-3 py-2 label-eyebrow">Part</th>
              <th className="px-3 py-2 label-eyebrow text-right">Quantity</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, i) => (
              <tr key={i} className={`border-b ${border} last:border-0`}>
                <td className="px-3 py-2 font-medium">{p.name}</td>
                <td className="px-3 py-2 text-right font-mono">{p.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
